import {createAsyncThunk} from "@reduxjs/toolkit";
import {handlerError} from "../../../store/handleError";
import {
    ICurrentMachinery,
    IFreezeState,
    ILockStatus,
    IMachinery,
    IInputPreset,
    IInputPresetQuery,
    IMachineryInput,
    IMachineryInputConfig,
    INewInputPreset,
    IMachineryListRow,
    IMachineryOutput,
    IMachineryOutputConfig,
    INewMachinery,
    IOutputCommandResult,
    OutputAction,
    OutputState,
} from "../../../models/iMachinery";
import {machineryAPI} from "../api";
import {MESSAGE_SEVERITY} from "../../../utils/const";
import {filesAPI} from "../../files/api";
import {thunkHandlers} from "../../../store/thunkHandlers";
import {AppDispatch, RootState} from "../../../store";
import {selectCurrentMachinery} from "./selectors";
import {setMessage} from "../../messages/model/slice";
import {setDocs} from "../../machinery_docs/model/slice";
import {setProblems} from "../../machinery_problems/model/slice";
import {setTasks} from "../../machinery_tasks/model/slice";
import {setComments} from "../../machinery_comments/model/slice";
import {setOrders} from "../../orders/model/slice";
import {updateLightListRow} from "./slice";

const messages = {
    addMachinery: {error: "Не удалось добавить машину.", success: "Машина добавлена"},
    deleteMachinery: {error: "Не удалось удалить машину.", success: "машина удалена"},
};

type ThunkConfig = {
    dispatch: AppDispatch;
    rejectValue: string;
};

interface IAddMachinery {
    newMachinery: INewMachinery;
    files: File[];
    /** Компания клиента (админ платформы заводит технику в чужую учётку). */
    companyId?: string;
}

export const fetchAddMachinery = createAsyncThunk<IMachinery, IAddMachinery, ThunkConfig>(
    "machinery/fetchAddMachinery",
    async (addMachineryData, {dispatch, rejectWithValue}) => {
        try {
            const {newMachinery, files, companyId} = addMachineryData;
            if (files.length > 0) {
                for (const file of files) {
                    const uploadedFile = await filesAPI.upload(file);
                    newMachinery.photos.push(uploadedFile);
                }
            }
            const res = await machineryAPI.add(newMachinery, companyId);
            thunkHandlers.success(messages.addMachinery.success, dispatch);
            return res;
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(
                setMessage({
                    severity: MESSAGE_SEVERITY.error,
                    text: errorMessage,
                }),
            );
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchGetAllMachinery = createAsyncThunk("machinery/get_all", async (_, {rejectWithValue}) => {
    try {
        return await machineryAPI.getAll();
    } catch (e) {
        return rejectWithValue(handlerError(e));
    }
});

export const fetchGetMachineryOptions = createAsyncThunk(
    "machinery/get_options",
    async (_, {rejectWithValue}) => {
        try {
            return await machineryAPI.getOptions();
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

/**
 * Пороги оповещений по умолчанию для машины.
 *
 * Отдельным запросом, а не вместе с машиной: карточку машины тянет каждый
 * экран, а дефолты нужны одной карточке порогов.
 *
 * Ошибку НЕ показываем тостом: без дефолтов карточка просто рисует прочерки,
 * как раньше, и ругаться на пользователя из-за необязательной подсказки
 * незачем.
 */
export const fetchGetThresholdDefaults = createAsyncThunk(
    "machinery/get_threshold_defaults",
    async (machinery_id: string, {rejectWithValue}) => {
        try {
            return await machineryAPI.getThresholdDefaults(machinery_id);
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchGetMachineryById = createAsyncThunk(
    "machinery/get_by_id",
    async (machinery_id: string, {rejectWithValue, dispatch}) => {
        try {
            const {
                docs,
                problems,
                tasks,
                comments,
                orders,
                ...machinery
            } = await machineryAPI.getById(machinery_id);
            dispatch(setDocs(docs));
            dispatch(setProblems(problems));
            dispatch(setTasks(tasks));
            dispatch(setComments(comments));
            dispatch(setOrders(orders));
            return machinery;
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchUpdateMachinery = createAsyncThunk(
    "machinery/update",
    async (machinery: ICurrentMachinery | IMachinery, {rejectWithValue, dispatch}) => {
        try {
            return await machineryAPI.update(machinery);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(
                setMessage({
                    severity: MESSAGE_SEVERITY.error,
                    text: errorMessage || "Не удалось обновить машину.",
                }),
            );
            return rejectWithValue(handlerError(e));
        }
    },
);

// Узкое обновление вида топлива машины (для флитового дашборда).
// Шлёт PUT /machinery {id, fuel_type} — без полного объекта, чтобы не затронуть
// operating/odometer. Ошибки — через глобальный Message, как у полного update.
export const fetchUpdateMachineryFuelType = createAsyncThunk<
    IMachinery,
    { id: string; fuel_type: string },
    ThunkConfig
>(
    "machinery/update_fuel_type",
    async ({id, fuel_type}, {rejectWithValue, dispatch}) => {
        try {
            return await machineryAPI.updateFuelType(id, fuel_type);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(
                setMessage({
                    severity: MESSAGE_SEVERITY.error,
                    text: errorMessage || "Не удалось обновить вид топлива.",
                }),
            );
            return rejectWithValue(handlerError(e));
        }
    },
);

interface IUpdateTankCapacitiesArg {
    id: string;
    fuel_tank_capacity_liters: number | null;
    fuel_tank_capacity_liters_2: number | null;
}

export const fetchUpdateMachineryTankCapacities = createAsyncThunk<IMachinery, IUpdateTankCapacitiesArg, ThunkConfig>(
    "machinery/update_tank_capacities",
    async ({id, fuel_tank_capacity_liters, fuel_tank_capacity_liters_2}, {rejectWithValue, dispatch}) => {
        try {
            return await machineryAPI.updateTankCapacities(id, {
                fuel_tank_capacity_liters,
                fuel_tank_capacity_liters_2,
            });
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(
                setMessage({
                    severity: MESSAGE_SEVERITY.error,
                    text: errorMessage || "Не удалось обновить объёмы баков.",
                }),
            );
            return rejectWithValue(handlerError(e));
        }
    },
);

interface IUpdateDispatchParamsArg {
    id: string;
    payload_capacity_kg: number | null;
    cargo_volume_m3: number | null;
    height_m: number | null;
    loading_methods: string[] | null;
    forklift_compatible: boolean;
    parking_lat: number | null;
    parking_lon: number | null;
}

// Узкое обновление dispatch-параметров машины (карточка «Диспетчеризация»),
// по образцу fetchUpdateMachineryTankCapacities.
export const fetchUpdateMachineryDispatchParams = createAsyncThunk<IMachinery, IUpdateDispatchParamsArg, ThunkConfig>(
    "machinery/update_dispatch_params",
    async (arg, {rejectWithValue, dispatch}) => {
        try {
            const {id, payload_capacity_kg, cargo_volume_m3, height_m, loading_methods, forklift_compatible, parking_lat, parking_lon} = arg;
            return await machineryAPI.updateDispatchParams(id, {
                payload_capacity_kg,
                cargo_volume_m3,
                height_m,
                loading_methods,
                forklift_compatible,
                parking_lat,
                parking_lon,
            });
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(
                setMessage({
                    severity: MESSAGE_SEVERITY.error,
                    text: errorMessage || "Не удалось обновить параметры диспетчеризации.",
                }),
            );
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchDeleteMachinery = createAsyncThunk<string, string, ThunkConfig>(
    "machinery/delete",
    async (machinery_id, {rejectWithValue, dispatch}) => {
        try {
            await machineryAPI.delete(machinery_id);
            thunkHandlers.success(messages.deleteMachinery.success, dispatch);
            return machinery_id;
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchUploadMachineryPhoto = createAsyncThunk<ICurrentMachinery, FileList, { state: RootState }>(
    "fetch_update_machinery_photo",
    async (files: FileList, {rejectWithValue, dispatch, getState}) => {
        try {
            const currentMachinery = selectCurrentMachinery(getState());
            if (!currentMachinery) return;
            const fileArr = Array.from(files);             // FileList -> File[]
            const uploads = fileArr.map(async (file, i) => {
                const fileName = await filesAPI.upload(file);
                return fileName as string;
            });
            const file_names = await Promise.all(uploads);

            const updatedMachinery = {
                ...currentMachinery,
                photos: [...currentMachinery.photos, ...file_names]
            };
            return dispatch(fetchUpdateMachinery(updatedMachinery)).unwrap();
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchDeleteMachineryPhoto = createAsyncThunk<ICurrentMachinery, string, { state: RootState }>(
    "fetch_delete_machinery_photo",
    async (deletePhotoName: string, {rejectWithValue, dispatch, getState}) => {
        try {
            const currentMachinery = selectCurrentMachinery(getState());
            if (!currentMachinery) return;
            await filesAPI.delete(deletePhotoName);
            const updatedMachinery = {
                ...currentMachinery,
                photos: [...currentMachinery.photos.filter((photo) => photo !== deletePhotoName)],
            };
            return dispatch(fetchUpdateMachinery(updatedMachinery)).unwrap();
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchGetMachineryByCompany = createAsyncThunk<IMachineryListRow[], string, ThunkConfig>(
    "machinery/get_by_company",
    async (companyId, {rejectWithValue, dispatch}) => {
        try {
            return await machineryAPI.getByCompany(companyId);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(
                setMessage({
                    severity: MESSAGE_SEVERITY.error,
                    text: errorMessage || "Не удалось загрузить технику клиента.",
                }),
            );
            return rejectWithValue(handlerError(e));
        }
    },
);

// ─── Управление выходами терминала ───────────────────────────────────────────

/**
 * Конфигурация четырёх выходов машины. Бэкенд всегда отдаёт ровно четыре
 * строки, достраивая отсутствующие дефолтом, — клиенту достраивать нечего.
 */
export const fetchMachineryOutputs = createAsyncThunk<
    IMachineryOutput[],
    string,
    ThunkConfig
>("machinery/fetchMachineryOutputs", async (id, {rejectWithValue}) => {
    try {
        return await machineryAPI.getOutputs(id);
    } catch (e) {
        return rejectWithValue(handlerError(e));
    }
});

/** Полная замена конфигурации: PUT принимает все четыре строки разом. */
export const saveMachineryOutputs = createAsyncThunk<
    IMachineryOutput[],
    {id: string; outputs: IMachineryOutputConfig[]},
    ThunkConfig
>("machinery/saveMachineryOutputs", async ({id, outputs}, {dispatch, rejectWithValue}) => {
    try {
        const res: IMachineryOutput[] = await machineryAPI.saveOutputs(id, outputs);
        thunkHandlers.success("Настройки выходов сохранены", dispatch);
        return res;
    } catch (e) {
        const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
        dispatch(setMessage({severity: MESSAGE_SEVERITY.error, text: errorMessage}));
        return rejectWithValue(handlerError(e));
    }
});

// ─── Настройка входов терминала ──────────────────────────────────────────────

/**
 * Конфигурация десяти входов машины. Бэкенд всегда отдаёт ровно десять строк,
 * достраивая отсутствующие дефолтом, и добавляет occupied_by_fuel — клиенту
 * достраивать нечего.
 */
export const fetchMachineryInputs = createAsyncThunk<
    IMachineryInput[],
    string,
    ThunkConfig
>("machinery/fetchMachineryInputs", async (id, {rejectWithValue}) => {
    try {
        return await machineryAPI.getInputs(id);
    } catch (e) {
        return rejectWithValue(handlerError(e));
    }
});

/** Полная замена конфигурации: PUT принимает все десять строк разом. */
export const saveMachineryInputs = createAsyncThunk<
    IMachineryInput[],
    {id: string; inputs: IMachineryInputConfig[]},
    ThunkConfig
>("machinery/saveMachineryInputs", async ({id, inputs}, {dispatch, rejectWithValue}) => {
    try {
        const res: IMachineryInput[] = await machineryAPI.saveInputs(id, inputs);
        thunkHandlers.success("Настройки входов сохранены", dispatch);
        return res;
    } catch (e) {
        // Тоста об ошибке здесь нет намеренно — в отличие от выходов: карточка
        // входов показывает текст 400 над таблицей, и тост дублировал бы его
        // вторым сообщением о той же ошибке.
        return rejectWithValue(handlerError(e));
    }
});

// ─── Шаблоны таблиц преобразования входов ────────────────────────────────────

/**
 * Список шаблонов. Грузится лениво — при открытии модалки точек, а не с
 * карточкой.
 *
 * С этапа 7 запрашивается с `company_id` машины: шаблоны делятся по компаниям
 * (§ 11.2), и список в сторе один на все карточки. Поэтому он ПЕРЕЗАПРАШИВАЕТСЯ
 * при каждом открытии модалки, а не кэшируется «по первому открытию»: иначе
 * после перехода на машину другой компании в селекте остались бы чужие
 * шаблоны. Строк десятки — запрос дешевле ключа по компании в сторе.
 */
export const fetchInputPresets = createAsyncThunk<
    IInputPreset[],
    IInputPresetQuery | void,
    ThunkConfig
>("machinery/fetchInputPresets", async (query, {rejectWithValue}) => {
    try {
        return await machineryAPI.getInputPresets(query ?? undefined);
    } catch (e) {
        return rejectWithValue(handlerError(e));
    }
});

export const createInputPreset = createAsyncThunk<
    IInputPreset,
    INewInputPreset,
    ThunkConfig
>("machinery/createInputPreset", async (body, {dispatch, rejectWithValue}) => {
    try {
        const res: IInputPreset = await machineryAPI.createInputPreset(body);
        thunkHandlers.success("Шаблон сохранён", dispatch);
        return res;
    } catch (e) {
        return rejectWithValue(handlerError(e));
    }
});

export const deleteInputPreset = createAsyncThunk<string, string, ThunkConfig>(
    "machinery/deleteInputPreset",
    async (id, {dispatch, rejectWithValue}) => {
        try {
            await machineryAPI.deleteInputPreset(id);
            thunkHandlers.success("Шаблон удалён", dispatch);
            // Возвращаем id, а не ответ сервера: DELETE отдаёт пустое тело, а
            // слайсу нужно, какую строку выкинуть из списка.
            return id;
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

/**
 * Итог команды словами оператора.
 *
 * HTTP-успех не значит, что выход переключился: терминал спит и выходит на
 * связь редкими окнами, поэтому `timeout` и `not_connected` — обычные исходы,
 * а не ошибки. Показываем их предупреждением и объясняем, чего ждать дальше:
 * при `timeout` состояние подтвердит следующий пакет с тегом 0x45, при
 * `not_connected` команду добьёт ресенд.
 *
 * Смотрим на ПОСЛЕДНИЙ результат: у импульса их два, и судьбу операции решает
 * выключающая команда. Пустой список — не успех: так бэкенд отвечает, когда
 * гасит дубль в пределах защитного окна и в эфир ничего не уходит.
 */
const outputCommandMessage = (
    res: IOutputCommandResult,
): {severity: MESSAGE_SEVERITY; text: string} => {
    const last = res.results?.[res.results.length - 1];

    if (!last) {
        return {
            severity: MESSAGE_SEVERITY.info,
            text: "Команда не отправлена: такая же ушла несколько секунд назад",
        };
    }
    if (last.status === "timeout") {
        return {
            severity: MESSAGE_SEVERITY.warning,
            text: "Терминал не ответил за 30 с — состояние уточнится по следующему пакету",
        };
    }
    if (last.status === "not_connected") {
        return {
            severity: MESSAGE_SEVERITY.warning,
            text: "Трекер не на связи — команда будет доставлена при выходе на связь",
        };
    }
    if (last.status === "confirmed") {
        return {
            severity: MESSAGE_SEVERITY.success,
            text: last.resultText
                ? `Терминал ответил: ${last.resultText}`
                : "Команда подтверждена",
        };
    }
    // Статус, о котором фронт ещё не знает: сообщаем факт отправки, но успехом
    // не называем — подтверждения у нас нет.
    return {severity: MESSAGE_SEVERITY.info, text: "Команда отправлена"};
};

/**
 * Команда на выход («Тест» в карточке). Сообщение об успехе несёт ответ
 * терминала (`OUT(3..0) = …`) — по нему видно фактическое состояние всех
 * четырёх выходов, а не только того, которым управляли.
 */
export const runMachineryOutputAction = createAsyncThunk<
    IOutputCommandResult,
    {id: string; action: OutputAction; state?: OutputState; password?: string},
    ThunkConfig
>(
    "machinery/runMachineryOutputAction",
    async ({id, action, state, password}, {dispatch, rejectWithValue}) => {
        try {
            const res: IOutputCommandResult = await machineryAPI.runOutputAction(id, action, {
                state,
                password,
            });
            dispatch(setMessage(outputCommandMessage(res)));
            return res;
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(setMessage({severity: MESSAGE_SEVERITY.error, text: errorMessage}));
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchLockMachinery = createAsyncThunk<ILockStatus, { id: string; password: string }, ThunkConfig>(
    "machinery/fetchLockMachinery",
    async ({ id, password }, { dispatch, rejectWithValue }) => {
        try {
            const res: ILockStatus = await machineryAPI.lock(id, password);
            // Мгновенно отражаем desired в таблице, не дожидаясь пакета трекера.
            dispatch(updateLightListRow({ id, lock_state: res.desired_state, lock_confirmed: false }));
            thunkHandlers.success("Команда блокировки отправлена", dispatch);
            return res;
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(setMessage({ severity: MESSAGE_SEVERITY.error, text: errorMessage }));
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchUnlockMachinery = createAsyncThunk<ILockStatus, string, ThunkConfig>(
    "machinery/fetchUnlockMachinery",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const res: ILockStatus = await machineryAPI.unlock(id);
            dispatch(updateLightListRow({ id, lock_state: res.desired_state, lock_confirmed: false }));
            thunkHandlers.success("Команда разблокировки отправлена", dispatch);
            return res;
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(setMessage({ severity: MESSAGE_SEVERITY.error, text: errorMessage }));
            return rejectWithValue(handlerError(e));
        }
    },
);

/**
 * Режим заморозки: основной трекер ACTIVE → FROZEN и обратно.
 *
 * Ответ содержит новый статус — им же и обновляем стор, не перезапрашивая
 * список: единственное, что изменилось, это статус одного трекера.
 * Строку таблицы патчим сразу (updateLightListRow), карточку — в слайсе.
 */
export const fetchFreezeMachinery = createAsyncThunk<IFreezeState, string, ThunkConfig>(
    "machinery/fetchFreezeMachinery",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const res: IFreezeState = await machineryAPI.freeze(id);
            dispatch(updateLightListRow({ id, tracker_status: res.status }));
            thunkHandlers.success("Техника переведена в режим заморозки", dispatch);
            return res;
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(setMessage({ severity: MESSAGE_SEVERITY.error, text: errorMessage }));
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchUnfreezeMachinery = createAsyncThunk<IFreezeState, string, ThunkConfig>(
    "machinery/fetchUnfreezeMachinery",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const res: IFreezeState = await machineryAPI.unfreeze(id);
            dispatch(updateLightListRow({ id, tracker_status: res.status }));
            thunkHandlers.success("Техника снята с заморозки", dispatch);
            return res;
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Неизвестная ошибка";
            dispatch(setMessage({ severity: MESSAGE_SEVERITY.error, text: errorMessage }));
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchLockStatus = createAsyncThunk<ILockStatus, string, ThunkConfig>(
    "machinery/fetchLockStatus",
    async (id, { rejectWithValue }) => {
        try {
            return await machineryAPI.getLockStatus(id);
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);